// ActivityRailResizeHandle.jsx

import React, { useCallback, useEffect, useRef } from 'react';

// CSS
import './ActivityRailResizeHandle.css';

/**
 * ActivityRailResizeHandle
 *
 * Drag handle on the left edge of the Activity rail.
 *
 * @param {number} widthPx - Current rail width.
 * @param {function} onWidthChange - Called during drag with next width.
 * @param {function} onWidthCommit - Called on pointer up with final width.
 * @returns {ReactNode}
 */
const ActivityRailResizeHandle = React.memo(({
  widthPx,
  onWidthChange,
  onWidthCommit,
}) => {
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(widthPx);

  const handlePointerDown = useCallback((event) => {
    event.preventDefault();
    draggingRef.current = true;
    startXRef.current = event.clientX;
    startWidthRef.current = widthPx;
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }, [widthPx]);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!draggingRef.current) return;
      // Dragging left edge: move left → wider rail.
      const delta = startXRef.current - event.clientX;
      onWidthChange?.(startWidthRef.current + delta);
    };

    const handlePointerUp = (event) => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      const delta = startXRef.current - event.clientX;
      onWidthCommit?.(startWidthRef.current + delta);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [onWidthChange, onWidthCommit]);

  return (
    <div
      className="activity-rail-resize-handle-container"
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize activity panel"
      onPointerDown={handlePointerDown}
    />
  );
});

ActivityRailResizeHandle.displayName = 'ActivityRailResizeHandle';

export default ActivityRailResizeHandle;
